/**
 * Animates the "Buy 3, save 15%" progress bar filling up whenever the cart
 * changes (add to cart, quick-add, quantity +/-, remove), instead of just
 * snapping straight to the new width. Designed to read like liquid being
 * poured in: a slow, visible ~1.6s fill (see FILL_DURATION_MS, which must
 * match the width transition duration in cart-progress-bar.liquid's
 * <style> block) plus a soft light sweep across the bar while it moves.
 *
 * The bar's markup is fully server-rendered by Liquid (snippets/cart-
 * progress-bar.liquid) with the final width already baked into inline
 * style on every AJAX re-render, so a freshly-inserted element has nothing
 * to transition from on its own -- the browser just paints it at the final
 * width. This script bridges that gap: it remembers the last percent it
 * saw for each context (the cart drawer vs. the /cart page), and when a
 * new bar node appears with a different target percent, it briefly resets
 * the new node back to the old percent (transitions disabled), forces a
 * reflow, then lets the CSS transition on .mat-progress__fill animate it
 * up to the real target. The drawer gets a short delay first so the
 * popup finishes sliding into view before the bar starts moving, per the
 * "show the drawer, then fill the bar live" request.
 *
 * Dawn re-renders the cart drawer's contents MORE THAN ONCE per single
 * add-to-cart action: cart-drawer.js's own add-to-cart flow swaps
 * #CartDrawer's HTML once, and then that swap publishes a cartUpdate
 * pub/sub event that CartDrawerItems (which subclasses the base CartItems
 * and independently subscribes to that same event) reacts to by doing a
 * SECOND, redundant fetch-and-replace of the drawer's items/footer. Each
 * pass inserts a brand new (never-before-seen) .mat-progress__fill node,
 * so naively animating the first one we see would animate a node that
 * gets thrown away moments later, while the node that actually stays on
 * screen never gets touched. To handle this, each newly-seen node for a
 * given context (drawer/page) gets a version token; when its scheduled
 * delay elapses we only proceed if it is still the latest token for that
 * context AND still attached to the document -- otherwise a newer render
 * has superseded it and that newer node's own check will do the animating.
 *
 * Never touches the discount amount, the 1/2/3 step logic, or the
 * automatic-discount behavior -- purely a visual transition on top of
 * markup that already renders correctly without this script.
 */
(function () {
  var FILL_SELECTOR = '.mat-progress__fill';
  var DRAWER_FILL_DELAY_MS = 450;
  var PAGE_FILL_DELAY_MS = 150;
  // Must match the "width" transition duration on .mat-progress__fill in
  // snippets/cart-progress-bar.liquid. Used only as a safety-net cleanup
  // for the .is-filling class in case a transitionend event is missed.
  var FILL_DURATION_MS = 1600;

  var prefersReducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Last known fill percent per context that has actually been committed
  // to the screen (only updated once we know a node survived the
  // cascading-re-render race described above), so a freshly-rendered bar
  // knows what to animate FROM. null means "not seen yet" (animate from 0).
  var state = { drawer: null, page: null };

  // Bumped every time a new, not-yet-processed node is found for a
  // context; only the node holding the CURRENT token when its own delay
  // elapses gets to animate.
  var pendingToken = { drawer: 0, page: 0 };

  function parsePercent(widthValue) {
    var match = /(-?\d+(?:\.\d+)?)/.exec(widthValue || '');
    return match ? parseFloat(match[1]) : 0;
  }

  function contextFor(el) {
    if (el.closest('#CartDrawer')) return 'drawer';
    // On the standalone /cart page, cart-progress-bar.liquid renders as a
    // SIBLING of #main-cart-items (both are children of the <cart-items>
    // custom element), not inside it -- so #main-cart-items is never an
    // ancestor of the fill bar. The <cart-items> element itself wraps the
    // whole section and is the reliable anchor for "page" context.
    if (el.closest('cart-items')) return 'page';
    return null;
  }

  function animateFill(el, fromPercent, toPercent) {
    var previousInlineTransition = el.style.transition;
    el.style.transition = 'none';
    el.style.width = fromPercent + '%';
    // Force a reflow so the browser commits the starting width before the
    // target width is applied, otherwise the two writes get batched and
    // there is nothing to transition from.
    // eslint-disable-next-line no-unused-expressions
    el.offsetHeight;
    el.style.transition = previousInlineTransition || '';

    // Drives the "liquid pour" light-sweep overlay (see the .is-filling
    // CSS rule) for exactly the duration of the width transition.
    el.classList.add('is-filling');
    var cleanupTimer = null;
    function onTransitionEnd(event) {
      if (event.target !== el || event.propertyName !== 'width') return;
      cleanup();
    }
    function cleanup() {
      el.classList.remove('is-filling');
      el.removeEventListener('transitionend', onTransitionEnd);
      if (cleanupTimer) clearTimeout(cleanupTimer);
    }
    el.addEventListener('transitionend', onTransitionEnd);
    // Fallback in case transitionend never fires (e.g. the tab was
    // backgrounded mid-animation), so the glint never gets stuck on.
    cleanupTimer = setTimeout(cleanup, FILL_DURATION_MS + 300);

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        el.style.width = toPercent + '%';
      });
    });
  }

  function handleFillNode(el, silent) {
    if (!el || el.hasAttribute('data-fill-seen')) return;
    el.setAttribute('data-fill-seen', 'true');

    var ctx = contextFor(el);
    if (!ctx) return;

    var targetPercent = parsePercent(el.style.width);

    // Initial page load: leave the bar exactly as the server rendered it,
    // just record the baseline so the next real change can animate from it.
    if (silent) {
      state[ctx] = targetPercent;
      return;
    }

    pendingToken[ctx] += 1;
    var myToken = pendingToken[ctx];
    var delay = ctx === 'drawer' ? DRAWER_FILL_DELAY_MS : PAGE_FILL_DELAY_MS;

    setTimeout(function () {
      // A newer render for this context arrived since we were scheduled,
      // or this node was already removed from the page -- a different
      // (newer) call to this same function owns the animation now.
      if (myToken !== pendingToken[ctx] || !document.body.contains(el)) return;
      if (prefersReducedMotion) return;

      var startPercent = state[ctx] === null ? 0 : state[ctx];
      state[ctx] = targetPercent;
      if (startPercent === targetPercent) return;

      animateFill(el, startPercent, targetPercent);
    }, delay);
  }

  function scan(silent) {
    document.querySelectorAll(FILL_SELECTOR + ':not([data-fill-seen])').forEach(function (el) {
      handleFillNode(el, silent);
    });
  }

  // If a bar disappears entirely (cart dropped to 0 mats, so cart-progress-
  // bar.liquid renders nothing), reset its context so the next time it
  // reappears it fills up from 0 again instead of jumping from a stale value.
  function checkForDisappearance() {
    if (!document.querySelector('#CartDrawer ' + FILL_SELECTOR)) state.drawer = null;
    if (!document.querySelector('cart-items ' + FILL_SELECTOR)) state.page = null;
  }

  function observe() {
    var observer = new MutationObserver(function () {
      checkForDisappearance();
      scan(false);
    });
    ['#CartDrawer', '#main-cart-items', 'cart-items'].forEach(function (selector) {
      var el = document.querySelector(selector);
      if (el) observer.observe(el, { childList: true, subtree: true });
    });
  }

  function init() {
    scan(true);
    observe();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
